import { IAttachmentFile } from "../interfaces/corporateEvent";
import {
	IQuoteFormMenuAndServicesInfo,
	IQuoteFormFinancialDetails,
	ICreateQuoteFormSection,
} from "../types/quoteFormTypes";
import { getFullName } from "./commonUtils";
import { iUserDetails } from "../types/userType";
import { calculateQuoteDetails } from "./quoteFormUtils";

/**
 * Calculate grid layout based on number of images
 */
export const calculateGridLayout = (
	images: any[],
): {
	gridClass: string;
	imageRows: any[][];
} => {
	const imageCount = images.length;

	if (imageCount === 0) {
		return { gridClass: "grid-1x1", imageRows: [] };
	}

	let gridClass: string;
	let imageRows: any[][] = [];

	switch (imageCount) {
		case 1:
			gridClass = "grid-1x1";
			imageRows = [images];
			break;
		case 2:
			gridClass = "grid-1x2";
			imageRows = [images];
			break;
		case 3:
			gridClass = "grid-1x3";
			imageRows = images.map((img) => [img]);
			break;
		case 4:
			gridClass = "grid-2x2";
			imageRows = [images.slice(0, 2), images.slice(2, 4)];
			break;
		default:
			gridClass = "grid-2x2";
			const firstFourImages = images.slice(0, 4);
			imageRows = [firstFourImages.slice(0, 2), firstFourImages.slice(2, 4)];
			break;
	}

	return { gridClass, imageRows };
};

/**
 * Create dynamic sections array with calculated grid classes
 */
export const createDynamicSections = (data: IQuoteFormMenuAndServicesInfo): any => {
	return {
		...data,
		sections: data.sections
			.map((section: ICreateQuoteFormSection) => {
				const originalSection = { ...section };
				const images = originalSection.attachments || [];
				const { gridClass, imageRows } = calculateGridLayout(images);

				return {
					title: originalSection.sectionTitle + ":",
					content: originalSection.content,
					gridClass,
					imageRows,
					displayOrder: originalSection.displayOrder,
				};
			})
			.sort((a, b) => a.displayOrder - b.displayOrder),
	};
};

/**
 * Generate HTML content for the quote preview
 */
export const generateQuotePreviewHTML = (
	financialData: IQuoteFormFinancialDetails,
	menuData: IQuoteFormMenuAndServicesInfo,
	userDetails: iUserDetails,
	showWatermark?: boolean,
	isCorporateEvent?: boolean,
): string => {
	// Process the sections with grid layouts
	const processedData = createDynamicSections(menuData);

	let subTotal = 0;
	let municipalityTax = 0;
	let calculatedVat = 0;
	let total = 0;

	if (showWatermark) {
		const quoteDetails = calculateQuoteDetails(financialData);
		subTotal = quoteDetails.subTotal;
		municipalityTax = quoteDetails.municipalityTax;
		calculatedVat = quoteDetails.calculatedVat;
		total = quoteDetails.total;
	} else {
		subTotal = financialData.amount as number;
		municipalityTax = financialData.municipalityTax as number;
		calculatedVat = financialData.vat as number;
		total = subTotal + municipalityTax + calculatedVat;
	}

	// Generate sections HTML
	const sectionsHTML = processedData.sections
		.map((section: any) => {
			const imagesHTML = section?.imageRows
				?.map(
					(row: any[]) =>
						`<div class="image-row">
        ${row
					.map(
						(img: IAttachmentFile) =>
							`<div class="image-item">
            <img src="${img.fileUrl || "/assets/placeholder.svg"}" alt="${img.fileName || "Menu item"}" onerror="this.style.backgroundColor='#F5F5F5';">
          </div>`,
					)
					.join("")}
      </div>`,
				)
				.join("");

			return `
      <div class="menu-section${section.imageRows.length === 0 ? " no-images" : ""}">
        <div class="menu-left">
          <div class="menu-title">${section.title}</div>
          ${section.content ? `<div class="menu-items">${section.content}</div>` : ""}
        </div>
        <div class="menu-right">
          ${
						section.imageRows.length > 0
							? `
            <div class="image-grid ${section.gridClass}">
              ${imagesHTML}
            </div>
          `
							: '<div class="no-images-placeholder"></div>'
					}
        </div>
      </div>
    `;
		})
		.join("");

	// Return the complete HTML with the branded quote template structure
	return `
  <!DOCTYPE html>
  <html lang="en" style="background-color: #F2EEDE;">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Branded Quote</title>
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&family=Playfair:ital,opsz,wght@0,5..1200,300..900;1,5..1200,300..900&display=swap"
          rel="stylesheet">
        <link href="https://fonts.cdnfonts.com/css/general-sans" rel="stylesheet">
        <meta http-equiv="X-UA-Compatible" content="IE=Edge" />
        <style>
          /* 1. CSS Variables */
          :root {
          --primary-color: #789B73;
          --secondary-color: #E4F2E2;
          --text-color: #22031F;
          --background-color: #F2EEDE;
          --white: #FFFFFF;
          --border-color: #D7DAE0;
          --light-gray: #F2F2F2;
          --dark-gray: #66707A;
          --text-color-secondary: #78828A;
          
          /* Responsive font sizes */
          --font-size-xs: clamp(0.75rem, 2vw, 0.875rem);
          --font-size-sm: clamp(0.875rem, 2.5vw, 1rem);
          --font-size-base: clamp(1rem, 3vw, 1.125rem);
          --font-size-lg: clamp(1.125rem, 3.5vw, 1.25rem);
          --font-size-xl: clamp(1.25rem, 4vw, 1.5rem);
          --font-size-2xl: clamp(1.5rem, 5vw, 2rem);
          --font-size-3xl: clamp(1.875rem, 6vw, 2.5rem);
          --font-size-4xl: clamp(2.25rem, 7vw, 3rem);
          
          /* Responsive spacing */
          --spacing-xs: clamp(0.25rem, 1vw, 0.5rem);
          --spacing-sm: clamp(0.5rem, 1.5vw, 0.75rem);
          --spacing-md: clamp(0.75rem, 2vw, 1rem);
          --spacing-lg: clamp(1rem, 2.5vw, 1.5rem);
          --spacing-xl: clamp(1.5rem, 3vw, 2rem);
          --spacing-2xl: clamp(2rem, 4vw, 3rem);
          --spacing-3xl: clamp(3rem, 5vw, 4rem);
          
          /* Responsive container widths */
          --container-padding: clamp(1rem, 3vw, 2rem);
          --container-max-width: min(100%, 1200px);
          }
          /* 2. Reset & Base Styles */
          * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          }
          html,
          body {
          font-family: 'General Sans', Arial, sans-serif;
          background-color: var(--background-color);
          color: var(--text-color);
          line-height: 1.4;
          margin: 0;
          padding: 0;
          height: fit-content;
          width: 100%;
          min-width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          }
          /* Browsers render <b>/<strong> bold (700); General Sans tops out at 500. */
          b,
          strong {
          font-weight: 500;
          }
          /* 3. Layout & Page Setup */
          @media screen {
          body {
          background-color: var(--background-color);
          }
          }
          @page {
          margin: 0px;
          background-color: var(--background-color);
          }
          @media print {
          html,
          body {
          background-color: var(--background-color) !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          }
          .menu-section:first-child {
          margin-top: 0 !important;
          }
          }
          * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          }
          .container {
          width: 100%;
          margin: 0;
          padding: 16px 0px 20px 0px;
          box-sizing: border-box;
          }
          .main-frame {
          background-color: var(--white);
          border-radius: 25px;
          padding: 24px 20px;
          width: 100%;
          min-width: 100%;
          max-width: 100%;
          margin: 36px 20px 0px 20px;
          box-sizing: border-box;
          position: relative;
          }
          .separator {
          width: 100%;
          height: 1px;
          background-color: var(--border-color);
          display: block;
          margin-top: 24px;
          }
          .footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-family: 'General Sans', sans-serif;
          font-size: 14px;
          padding: 0px 10px 10px 10px;
          color: var(--text-color);
          position: absolute;
          bottom: 7px;
          left: 10px;
          right: 10px;
          }
          .footer .note {
          font-style: italic;
          }
          .footer .page-number::before {
          content: "Page " counter(page) " of " counter(pages);
          font-weight: 500;
          font-style: italic;
          }
          .content {
          width: 100%;
          padding: 0;
          margin: 0;
          /* margin-top: 246px; */
          }
          .content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          max-width: 100%;
          padding-bottom: 24px;
          }
          .vendor-info {
          display: flex;
          align-items: center;
          gap: 16px;
          }
          .vendor-logo {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          object-fit: contain;
          border: 1px solid var(--border-color);
          }
          .vendor-name {
          font-family: 'Playfair Display', serif;
          font-weight: 600;
          font-size: 32px;
          color: var(--text-color);
          line-height: 1.84;
          }
          .youcater-logo {
          width: 190px;
          object-fit: cover;
          }
          .title-section {
          margin-bottom: 26px;
          padding: 10px 0;
          border-bottom: 1px solid var(--border-color);
          position: relative;
          }
          .title {
          font-family: 'General Sans', sans-serif;
          font-weight: 500;
          font-size: 21px;
          color: var(--dark-gray);
          margin: 10px 0;
          text-align: left;
          }
          .title::before,
          .title::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          height: 1px;
          background-color: var(--border-color);
          }
          .title::before {
          top: 0;
          }
          .title::after {
          bottom: 0;
          }
          .menu-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin: 0 0px 13px 0px;
          }
          .menu-content .content-header {
          min-width: 100%;
          padding-bottom: 0;
          }
          .menu-section {
          background-color: var(--light-gray);
          border-radius: 10px;
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: stretch;
          gap: 24px;
          min-height: 165px;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          }
          /* Remove top margin for first section to avoid double spacing */
          .menu-section:first-child {
          margin-top: 0;
          }
          /* Special styling for sections with no images */
          .menu-section.no-images {
          justify-content: flex-start;
          }
          .menu-section.no-images .menu-left {
          width: 100%;
          max-width: 100%;
          flex: none;
          }
          .menu-section.no-images .menu-right {
          display: none;
          }
          .menu-left {
          flex: 1;
          padding: 0 8px;
          min-height: 165px;
          display: flex;
          flex-direction: column;
          width: 50%;
          max-width: 50%;
          justify-content: center;
          box-sizing: border-box;
          }
          .menu-title {
          font-family: 'General Sans', sans-serif;
          font-weight: 500;
          font-size: 32px;
          line-height: 1.36;
          color: var(--text-color);
          margin-bottom: 12px;
          }
          .menu-title.salads {
          font-weight: 500;
          }
          .menu-items {
          font-family: 'General Sans', sans-serif;
          font-weight: 400;
          font-size: 18px;
          line-height: 1.5;
          color: var(--text-color);
          margin: 0;
          padding: 0;
          }
          .menu-items ul, ol {
          padding-left: 16px;
          }
          .menu-right {
          flex-shrink: 0;
          min-height: 300px;
          display: flex;
          width: 50%;
          max-width: 50%;
          box-sizing: border-box;
          }
          .image-grid {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 8px;
          }
          .image-row {
          display: flex;
          gap: 8px;
          }
          .image-item {
          border-radius: 8px;
          overflow: hidden;
          background-color: var(--white);
          }
          /* Default image grid styles */
          .menu-section .image-grid {
          min-width: 100%;
          max-width: 100%;
          width: 100%;
          }
          .menu-section .image-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          }
          /* Grid: 1x1 (1 image) */
          .grid-1x1 {
          width: 100%;
          max-width: 100%;
          height: calc(364px - 32px);
          min-height: calc(364px - 32px);
          justify-content: center;
          align-items: center;
          background-color: var(--white);
          box-sizing: border-box;
          }
          .grid-1x1 .image-row {
          justify-content: center;
          width: 100%;
          height: -webkit-fill-available;
          }
          .grid-1x1 .image-item {
          width: 100%;
          height: 100%;
          }
          /* Grid: 1x2 (2 images horizontal) */
          .grid-1x2 {
          width: 100%;
          max-width: 100%;
          height: calc(364px - 32px);
          min-height: calc(364px - 32px);
          justify-content: center;
          align-items: center;
          background-color: var(--white);
          box-sizing: border-box;
          }
          .grid-1x2 .image-row {
          justify-content: center;
          width: 100%;
          height: 100%;
          }
          .grid-1x2 .image-item {
          width: 100%;
          height: 100%;
          }
          /* Grid: 1x3 (3 images vertical) */
          .grid-1x3 {
          width: 100%;
          max-width: 100%;
          height: calc(364px - 32px);
          min-height: calc(364px - 32px);
          flex-direction: row;
          justify-content: space-between;
          background-color: var(--white);
          box-sizing: border-box;
          }
          .grid-1x3 .image-row {
          justify-content: center;
          width: 100%;
          }
          .grid-1x3 .image-item {
          width: 100%;
          height: 100%;
          }
          /* Grid: 1x4 (4 images vertical) */
          .grid-1x4 {
          width: 100%;
          max-width: 100%;
          height: calc(364px - 32px);
          flex-direction: column;
          justify-content: space-between;
          box-sizing: border-box;
          }
          .grid-1x4 .image-row {
          justify-content: center;
          width: 100%;
          }
          .grid-1x4 .image-item {
          width: 100%;
          height: 65px;
          }
          /* Grid: 2x1 (2 images vertical) */
          .grid-2x1 {
          width: 100%;
          max-width: 100%;
          height: calc(364px - 32px);
          flex-direction: column;
          justify-content: space-between;
          box-sizing: border-box;
          }
          .grid-2x1 .image-row {
          justify-content: center;
          width: 100%;
          }
          .grid-2x1 .image-item {
          width: 100%;
          height: 140px;
          }
          /* Grid: 2x2 (4 images in 2x2 grid) */
          .grid-2x2 {
          min-width: 100%;
          max-width: 100%;
          width: 100%;
          height: calc(364px - 32px);
          min-height: calc(364px - 32px);
          background-color: var(--white);
          }
          .grid-2x2 .image-row {
          justify-content: space-between;
          width: 100%;
          }
          .grid-2x2 .image-item {
          width: calc(50% - 4px);
          min-height: 154px;
          height: 154px;
          }
          /* Grid: 3x1 (3 images horizontal) */
          .grid-3x1 {
          width: 100%;
          max-width: 100%;
          height: 100px;
          justify-content: space-between;
          align-items: center;
          box-sizing: border-box;
          }
          .grid-3x1 .image-row {
          justify-content: space-between;
          width: 100%;
          }
          .grid-3x1 .image-item {
          width: calc(33.33% - 4px);
          height: 100px;
          }
          /* Grid: 4x1 (4 images horizontal) */
          .grid-4x1 {
          width: 100%;
          max-width: 100%;
          height: 80px;
          justify-content: space-between;
          align-items: center;
          box-sizing: border-box;
          }
          .grid-4x1 .image-row {
          justify-content: space-between;
          width: 100%;
          }
          .grid-4x1 .image-item {
          width: calc(25% - 3px);
          height: 80px;
          }
          /* No images placeholder */
          .no-images-placeholder {
          display: none;
          }
          .invoice-container {
          width: 100%;
          margin: 20px auto 0 auto;
          }
          .invoice-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
          gap: 20px;
          flex-wrap: wrap;
          }
          .detail-group {
          width: calc(36% - 40px);
          flex-shrink: 0;
          }
          .detail-group:nth-child(3n) {
          width: calc(24% - 40px);
          }
          .detail-label {
          font-weight: 500;
          margin-bottom: 5px;
          font-size: 16px;
          font-family: 'General Sans', sans-serif;
          font-weight: normal;
          color: var(--text-color-secondary);
          }
          .detail-value {
          color: var(--text-color);
          font-size: 20px;
          font-family: 'General Sans', sans-serif;
          font-weight: 500;
          }
          .invoice-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
          }
          .invoice-table thead {
          background-color: var(--light-gray);
          height: 68px;
          }
          .invoice-table thead th {
          font-size: 20px;
          font-weight: 500;
          color: var(--dark-gray)
          }
          .invoice-table th {
          padding: 12px;
          text-align: left;
          font-weight: 500;
          }
          .invoice-table td {
          padding: 26px 12px;
          border-bottom: 1px solid #eee;
          }
          .invoice-table th:first-child,
          .invoice-table td:first-child {
          width: 50px;
          text-align: center;
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          }
          .invoice-table th:last-child,
          .invoice-table td:last-child {
          text-align: right;
          min-width: 120px;
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          }
          .item-name {
          font-family: 'General Sans', sans-serif;
          font-size: 22px;
          font-weight: 500;
          }
          .item-description {
          color: var(--text-color-secondary);
          font-size: 18px;
          font-weight: 400;
          margin-top: 2px;
          font-family: 'General Sans', sans-serif;
          }
          .amount {
          font-family: 'General Sans', sans-serif;
          font-size: 20px;
          font-weight: normal;
          text-align: right;
          color: var(--text-color);
          }
          .totals-section {
          display: flex;
          justify-content: flex-end;
          margin-top: 20px;
          }
          .totals-table {
          width: 416px;
          border-collapse: collapse;
          }
          .totals-table td {
          padding: 8px 12px;
          }
          .totals-table td:first-child {
          text-align: left;
          }
          .totals-table td:last-child {
          text-align: right;
          min-width: 100px;
          }
          .grand-total {
          font-weight: 500;
          font-size: 16px;
          }
          .subtotal-row td {
          font-family: 'General Sans', sans-serif;
          font-size: 20px;
          font-weight: 500;
          }
          .grand-total td {
          font-family: 'General Sans', sans-serif;
          font-size: 24px;
          font-weight: 500;
          }
          /* Terms and Conditions Section */
          .terms-section {
          margin-top: 40px;
          page-break-inside: avoid;
          break-inside: avoid;
          }
          .terms-header {
          margin-bottom: 4px;
          }
          .terms-title {
          font-family: 'General Sans', sans-serif;
          font-weight: 500;
          font-size: 32px;
          line-height: 1.364;
          color: var(--text-color);
          margin: 0;
          }
          .terms-intro {
          margin-bottom: 20px;
          }
          .terms-item {
          margin-bottom: 20px;
          page-break-inside: avoid;
          break-inside: avoid;
          }
          /* Prevent the last terms item from breaking */
          .terms-item:last-child {
          page-break-inside: avoid;
          break-inside: avoid;
          page-break-after: avoid;
          break-after: avoid;
          }
          .terms-text {
          font-family: 'General Sans', sans-serif;
          font-weight: 400;
          font-size: 20px;
          line-height: 1.6;
          color: var(--text-color);
          margin: 0;
          padding: 0;
          text-align: left;
          }
          .terms-text strong {
          font-weight: 500;
          }
          .terms-text br {
          line-height: 1.6;
          }
          .watermark
          {
          position:absolute;
          top:50%;
          left:0;
          right:0;
          width:100%;
          z-index:99;
          color:#D3D3D3;
          font-size: calc(100vw / 15);
          font-weight: 500;
          font-family: 'General Sans', sans-serif;
          transform: translateY(-50%) rotate(-45deg);
          opacity: 0.7;
          text-align: center;
          }
          @media (max-width: 1024px) {
          .main-frame {
          width: calc(100vw - 112px);
          }
          .container {
          padding: var(--spacing-md);
          }
          .menu-section {
          padding: var(--spacing-md);
          }
          .invoice-table {
          font-size: var(--font-size-sm);
          }
          .invoice-table th,
          .invoice-table td {
          padding: var(--spacing-sm);
          }
          }
          }
          
          /* Scaling approach - maintain original layout but scale based on viewport */
          @media (max-width: 1200px) {
          .main-frame {
          transform: scale(0.8);
          transform-origin: top left;
          min-width: 125%;
          margin: 0;
          }
          }
          
          @media (max-width: 899px) {
          .main-frame {
          transform: scale(0.6);
          transform-origin: top left;
          min-width: 167%;
          margin: 0;
          height: ${showWatermark ? "122vh" : "125vh"};
          overflow-y: auto;
          overflow-x: hidden;
          }
          }
          
          @media (max-width: 599px) {
          .main-frame {
          transform: scale(0.4);
          transform-origin: top left;
          min-width: 250%;
          margin: 0;
          height: ${showWatermark ? "175vh" : "190vh"};
          }
          }
          
          @media (max-width: 399px) {
          .main-frame {
          transform: scale(0.3);
          transform-origin: top left;
          min-width: 330%;
          margin: 0;
          height: ${showWatermark ? "230vh" : "250vh"};
          }
          }
        </style>
    </head>
    <body>
        ${
					showWatermark
						? `<div class="watermark">
                PREVIEW ONLY
              </div>`
						: ""
				}
        <div class="main-frame">
          <!-- Header -->
          <div class="content-header">
              <div class="vendor-info">
                <img class="vendor-logo" src="${userDetails?.logo}" alt="vendor logo">
                <div class="vendor-name">${getFullName(userDetails?.firstName, userDetails?.lastName)}</div>
              </div>
              <div class="youcater-logo">
                <img src="/assets/dashboard/youCater${isCorporateEvent ? "Business" : "Logo"}.png" alt="youCATER" style="width: 100%; height: 100%; object-fit: contain;">
              </div>
          </div>
          <div class="container">
              <div class="content">
                <!-- Title Section -->
                <div class="title-section">
                    <div class="title">Menu & Services Information</div>
                </div>
                <!-- Menu Content -->
                <div class="menu-content">
                    ${sectionsHTML}
                    <div class="separator"></div>
                    <div class="content-header">
                      <div class="vendor-info">
                          <img class="vendor-logo" src="${userDetails?.logo}" alt="vendor logo">
                          <div class="vendor-name">${getFullName(userDetails?.firstName, userDetails?.lastName)}</div>
                      </div>
                      <div class="youcater-logo">
                          <img src="/assets/dashboard/youCater${isCorporateEvent ? "Business" : "Logo"}.png" alt="youCATER" style="width: 100%; height: 100%; object-fit: contain;">
                      </div>
                    </div>
                    <div class="separator" style="margin-top: 0px;"></div>
                    <div class="invoice-container">
                      <!-- Invoice Header -->
                      <div class="invoice-header">
                          <div class="detail-group">
                            <h2 class="detail-label">Billed to</h2>
                            <p class="detail-value">******************</p>
                          </div>
                          <div class="detail-group">
                            <div class="detail-label">TRN</div>
                            <div class="detail-value">******************</div>
                          </div>
                          <div class="detail-group">
                            <div class="detail-label">Payment Terms</div>
                            <div class="detail-value">******************</div>
                          </div>
                          <div class="detail-group">
                            <div class="detail-label">Shipping/Billing Address</div>
                            <div class="detail-value">******************</div>
                          </div>
                          <div class="detail-group">
                            <div class="detail-label">Event Start Date</div>
                            <div class="detail-value">******************</div>
                          </div>
                          <div class="detail-group"></div>
                      </div>
                      <!-- Invoice Items Table -->
                      <table class="invoice-table">
                          <thead>
                            <tr>
                                <th>#</th>
                                <th>Item Name</th>
                                <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                                <td class="row-count">1</td>
                                <td>
                                  <div class="item-name">Quoted Amount</div>
                                </td>
                                <td class="amount">AED ${subTotal?.toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td class="row-count">2</td>
                                <td>
                                  <div class="item-name">Municipality Tax Amount</div>
                                </td>
                                <td class="amount">AED ${municipalityTax?.toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td class="row-count">3</td>
                                <td>
                                  <div class="item-name">VAT Amount</div>
                                </td>
                                <td class="amount">AED ${calculatedVat?.toFixed(2)}</td>
                            </tr>
                          </tbody>
                      </table>
                      <!-- Totals Section -->
                      <div class="totals-section">
                          <table class="totals-table">
                            <tr class="subtotal-row">
                                <td>Subtotal</td>
                                <td>AED ${subTotal?.toFixed(2)}</td>
                            </tr>
                            <tr class="grand-total">
                                <td>Grand Total</td>
                                <td>AED ${total?.toFixed(2)}</td>
                            </tr>
                          </table>
                      </div>
                    </div>
                </div>
              </div>
          </div>
        </div>
    </body>
  </html>
    `;
};
